import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UseraccessserviceService } from 'src/app/services/useraccessservice.service';
import { OfflineContentReaderService } from 'src/app/services/offline/offline-content-reader.service';
import { OfflineConnectivityService } from 'src/app/services/offline/offline-connectivity.service';
import { getCurrentSchoolSubdomain, getCurrentUserId } from 'src/app/services/offline/offline-cache-key';
import { FullscreenNoteChrome } from 'src/app/services/fullscreen-note-chrome';
import { OfflineDbService } from 'src/app/services/offline/offline-db.service';
import { OfflineMediaService } from 'src/app/services/offline/offline-media.service';
import { MediaRecord } from 'src/app/services/offline/offline-db.types';
import { withTimeout } from 'src/app/services/offline/offline-network-failure';
import { LessonGamePlay } from 'src/app/models/module/lessongame.model';

interface NotePage {
  page: number;
  title: string;
  html: SafeHtml;
  empty: boolean;
  // Set for a lesson game's page instead of note HTML. A saved game carries its answer key,
  // so attempts are checked on the device (check-game-attempt.ts) - nothing is recorded,
  // exactly as online.
  game?: LessonGamePlay;
}

// The dedicated read-only lesson/note reader for the downloads hub — reads straight from
// IndexedDB via OfflineContentReaderService, no HttpClient calls at all. Mirrors the online
// lesson page's note pager (class/details/details.component.ts's blocks/currentBlock/
// nextPage/previousPage) for a consistent experience, but notes only — no games, no
// assessments, no admin actions, no cross-lesson next/prev (none of that is fetchable or
// meaningful offline; LessonDetailsModel carries no page-block/game data at all, so the
// exclusion falls out naturally). The only way out of this page is back to the hub (a fixed
// routerLink, not history-back), so "no other navigation" holds regardless of entry point.
@Component({
  selector: 'app-downloadedlesson',
  templateUrl: './downloadedlesson.component.html',
  styleUrls: ['./downloadedlesson.component.css'],
})
export class DownloadedlessonComponent implements OnInit, OnDestroy {
  loading = true;
  notFound = false;
  lessonTitle = '';
  classId = '';
  moduleId: number | null = null;

  pages: NotePage[] = [];
  currentPage = 1;
  get currentBlock(): NotePage | null {
    return this.pages[this.currentPage - 1] ?? null;
  }

  // The live lesson page (class/details/details.component.ts) needs a real connection for
  // almost everything on it — games, assessments, next/prev, admin actions — so only offer
  // the way back to it while there's actually a connection to use. Offline, this link would
  // just get caught by the app-wide block overlay anyway; hiding it here is a clearer signal
  // than showing something that would immediately bounce.
  get canGoToLiveLesson(): boolean {
    return this.connectivity.currentState !== 'offline';
  }

  constructor(
    private route: ActivatedRoute,
    private useraccessservice: UseraccessserviceService,
    private contentReader: OfflineContentReaderService,
    private connectivity: OfflineConnectivityService,
    private sanitizer: DomSanitizer,
    private offlineDb: OfflineDbService,
    private offlineMedia: OfflineMediaService,
  ) {}

  // blob: URLs handed to the note's video players, revoked when the reader closes.
  private videoObjectUrls: string[] = [];

  async ngOnInit(): Promise<void> {
    const classId = this.route.snapshot.queryParamMap.get('classid');
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());

    if (!school || !user || !classId) {
      this.notFound = true;
      this.loading = false;
      return;
    }
    this.classId = classId;

    try {
      const lessonContent = await this.contentReader.read<any>(school, user, 'lesson', classId);
      if (!lessonContent) {
        this.notFound = true;
        return;
      }
      this.lessonTitle = lessonContent.title;
      this.moduleId = lessonContent.moduleid;

      const noteContent = await this.contentReader.read<any[]>(school, user, 'note', classId);
      const savedVideos = await withTimeout(this.offlineDb.listMediaManifests(school, user), 3000, 'saved video list').catch((): MediaRecord[] => []);
      const sorted = (noteContent ?? []).slice().sort((a, b) => (a.page ?? 0) - (b.page ?? 0));
      const pages: NotePage[] = [];
      for (const [i, n] of sorted.entries()) {
        const emptyContent = !n.safeContent || n.safeContent === '<p><br></p>';
        const html = emptyContent ? '' : await this.embedSavedVideos(n.safeContent, savedVideos);
        pages.push({
          page: n.page ?? i + 1,
          title: n.title?.length > 0 ? n.title : `Section ${i + 1}`,
          html: this.sanitizer.bypassSecurityTrustHtml(html),
          empty: emptyContent,
        });
      }
      // Games interleaved by page exactly as the live lesson page does (details.component.ts
      // buildBlocks): ordered by page, and a game goes before a note on the same page.
      const games = (await this.contentReader.read<LessonGamePlay[]>(school, user, 'lessongames', classId)) ?? [];
      for (const game of games) {
        pages.push({ page: game.page || 0, title: game.title || 'Lesson game', html: this.sanitizer.bypassSecurityTrustHtml(''), empty: false, game });
      }
      // Compare the KIND (game or note), never the objects: two games on one page must keep the
      // server's order, which a stable sort only preserves when it reports them equal.
      pages.sort((a, b) => (a.page !== b.page ? a.page - b.page : !!a.game === !!b.game ? 0 : a.game ? -1 : 1));
      this.pages = pages;
    } finally {
      this.loading = false;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) this.currentPage--;
  }

  nextPage(): void {
    if (this.currentPage < this.pages.length) this.currentPage++;
  }

  gotoPage(page: number): void {
    this.currentPage = page;
  }

  /* Full-screen reading. Same helper as the live lesson page and the note editor, so the
     three behave identically. */
  readonly fs = new FullscreenNoteChrome();

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.fs.active) {
      this.fs.set(false);
    }
  }

  // A note embeds its videos as <iframe src="/media?moduleid=…&uploadid=…">. Each such frame is
  // a fresh page load that boots the whole app again — offline, that depends on the service
  // worker serving index.html and everything it references, some of it from other hosts the
  // worker cannot serve (fonts, cdnjs). When any of that fails the frame stays white, while
  // this page, already running, looks fine. So here, where everything is read from the device,
  // a saved video is played in place by a native <video> reading the saved file — no frame, no
  // page load, nothing that needs a network or a service worker.
  //
  // Videos with no saved copy keep their frame while online (it streams), and are replaced by a
  // plain explanation while offline, instead of a frame that can only fail.
  private async embedSavedVideos(html: string, savedVideos: MediaRecord[]): Promise<string> {
    if (!html.includes('<iframe')) return html;
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    // navigator.onLine is unreliable when it says true, but never wrong when it says false - and
    // the connectivity state can lag behind it (airplane mode switched on while the app was in
    // the background fires no request to fail). Either one saying offline is enough; keeping a
    // streaming frame while offline is what leaves a spinner that never ends.
    const offline = navigator.onLine === false || this.connectivity.currentState !== 'online';

    for (const frame of Array.from(doc.querySelectorAll('iframe'))) {
      let url: URL;
      try {
        url = new URL(frame.getAttribute('src') ?? '', window.location.origin);
      } catch {
        continue;
      }
      if (url.origin !== window.location.origin || url.pathname !== '/media') continue;
      const moduleid = url.searchParams.get('moduleid');
      const uploadid = url.searchParams.get('uploadid');
      if (!moduleid || !uploadid) continue;

      const manifest = savedVideos.find((m) => m.resourceId === `${moduleid}:${uploadid}`);
      const file = manifest ? await this.offlineMedia.openSavedVideo(manifest).catch((): File | null => null) : null;

      if (file) {
        // Explicit type: Chrome infers it from the file name, other engines may not.
        const objectUrl = URL.createObjectURL(file.slice(0, file.size, manifest.mimeType || 'video/mp4'));
        this.videoObjectUrls.push(objectUrl);
        const video = doc.createElement('video');
        video.setAttribute('src', objectUrl);
        video.setAttribute('controls', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');
        video.className = 'offline-note-video';
        frame.replaceWith(video);
      } else if (offline || manifest) {
        // Two different situations, deliberately worded differently so a tester can tell them
        // apart on a phone: never saved, versus saved but the file would not open.
        const note = doc.createElement('p');
        note.className = 'offline-note-video-missing';
        note.textContent = manifest
          ? 'This video was saved, but the saved file could not be opened on this device. Try again, or save the video again while online.'
          : "This video has not been saved on this device yet. While online, open the lesson and tap 'Save for offline' again.";
        frame.replaceWith(note);
      }
    }
    return doc.body.innerHTML;
  }

  ngOnDestroy(): void {
    for (const objectUrl of this.videoObjectUrls) URL.revokeObjectURL(objectUrl);
    this.videoObjectUrls = [];
    // The scroll lock is on <body>, which outlives this component.
    this.fs.destroy();
  }
}
